import { type BedKey, type BedQuery, ENTITY_CLASS } from "@packages/core";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { Temporal } from "temporal-polyfill";

import { fetchData, findBeds } from "./functions";
import { QueryKey } from "./query";
import { NETWORK_SEMAPHORE } from "./semaphore";
import { TANSTACK_QUERY_CLIENT } from "./tanstack";

export async function findBed(bedKey: BedKey | false | null | undefined) {
  if (!bedKey) {
    return undefined;
  }
  const { output } = await fetchData({ type: "Entity::Bed", input: { bedKey } });
  if (output instanceof Error) {
    throw output;
  } else {
    return output;
  }
}

function BedQueryKey(bedKey: BedKey | false | null | undefined) {
  return QueryKey(ENTITY_CLASS.BED, bedKey);
}

export function updateBedQueryData(
  bedKey: BedKey | false | null | undefined,
  newBed: ReturnType<typeof useBedQuery>["data"] | false,
) {
  if (bedKey && newBed !== false && newBed !== undefined) {
    const queryKey = BedQueryKey(bedKey);
    TANSTACK_QUERY_CLIENT.setQueryData(
      queryKey,
      function (oldBed: ReturnType<typeof useBedQuery>["data"]) {
        return !oldBed ||
          !newBed ||
          (Temporal.PlainDateTime.compare(oldBed.createdAt, newBed.createdAt) ||
            Temporal.PlainDateTime.compare(oldBed.updatedAt, newBed.updatedAt)) < 0
          ? newBed
          : oldBed;
      },
    );
  }
}

export function useBedQuery(bedKey: BedKey | false | null | undefined) {
  return useQuery({
    queryKey: BedQueryKey(bedKey),
    async queryFn() {
      return findBed(bedKey);
    },

    enabled: Boolean(bedKey),
    refetchInterval: false,
    refetchIntervalInBackground: false,
    refetchOnMount: true,
    refetchOnReconnect: true,
    refetchOnWindowFocus: true,
    staleTime: /** 4時間 */ 14_400_000,
  });
}

function BedInfiniteQueryKey(...parameters: Parameters<typeof useBedInfiniteQuery>) {
  return QueryKey(ENTITY_CLASS.BED + "List", ...parameters);
}

export function resetBedInfiniteQueries() {
  TANSTACK_QUERY_CLIENT.resetQueries({ queryKey: BedInfiniteQueryKey({}).slice(0, 1) });
}

export function useBedInfiniteQuery(
  query: BedQuery,
  options?: Partial<{
    /** 処理を実行する場合に`true`を設定するフラグ */
    enabled: boolean;
  }>,
) {
  return useInfiniteQuery({
    queryKey: BedInfiniteQueryKey(query, options),
    async queryFn(context) {
      const release = await NETWORK_SEMAPHORE.acquire();
      try {
        const { beds, errors, nextPageToken } = await findBeds({ ...query, ...context.pageParam });

        for (const bed of beds) {
          updateBedQueryData(bed.bedKey, bed);
        }

        return { beds, errors, nextPageToken };
      } finally {
        release();
      }
    },

    getNextPageParam(lastPage) {
      return lastPage.nextPageToken ? { nextPageToken: lastPage.nextPageToken } : undefined;
    },
    initialPageParam: {} as { nextPageToken?: Record<string, any> },

    enabled: options?.enabled,
    refetchInterval: false,
    refetchIntervalInBackground: false,
    refetchOnMount: true,
    refetchOnReconnect: true,
    refetchOnWindowFocus: true,
    staleTime: /** 4時間 */ 14_400_000,
  });
}
